/** Process sample on one command attempt. The installed API client does not declare `runtime` yet. */
export interface CommandExecutionRuntime {
  startedWorkingSetBytes?: number | null;
  workingSetBytes?: number | null;
  workingSetDeltaBytes?: number | null;
  startedManagedBytes?: number | null;
  managedBytes?: number | null;
  managedDeltaBytes?: number | null;
  startedGcHeapBytes?: number | null;
  gcHeapBytes?: number | null;
  gcHeapDeltaBytes?: number | null;
  startedCommittedBytes?: number | null;
  committedBytes?: number | null;
  committedDeltaBytes?: number | null;
  startedFragmentedBytes?: number | null;
  fragmentedBytes?: number | null;
  fragmentedDeltaBytes?: number | null;
  startedPrivateBytes?: number | null;
  privateBytes?: number | null;
  privateDeltaBytes?: number | null;
  allocatedBytes?: number | null;
  gen0Collections?: number | null;
  gen1Collections?: number | null;
  gen2Collections?: number | null;
  cpuMilliseconds?: number | null;
}

export interface CommandRuntimeRow {
  label: string;
  value: string;
}

export interface CommandRuntimeView {
  pending: boolean;
  rows: CommandRuntimeRow[];
  rssDeltaLabel: string | null;
}

export interface AssignmentRuntimeView {
  highestRss: string;
  highestStep: string;
  highestAttempt: string;
  change: string | null;
  changeStep: string | null;
  changeAttempt: string | null;
}

export interface AssignmentRuntimeCommand {
  stepKey: string;
  attempt: number | string;
  runtime: CommandExecutionRuntime | null;
}

const KIB = 1024;
const MIB = 1024 * 1024;
const GIB = 1024 * 1024 * 1024;

export function readCommandRuntime(execution: object): CommandExecutionRuntime | null {
  const runtime = (execution as { runtime?: CommandExecutionRuntime | null }).runtime;
  return runtime ?? null;
}

export function commandRuntimeView(runtime: CommandExecutionRuntime | null): CommandRuntimeView | null {
  if (!runtime || (runtime.startedWorkingSetBytes == null && runtime.workingSetBytes == null)) {
    return null;
  }
  const pending = runtime.workingSetBytes == null;
  const rows: CommandRuntimeRow[] = [
    { label: 'RSS', value: byteSpan(runtime.startedWorkingSetBytes, runtime.workingSetBytes, runtime.workingSetDeltaBytes) },
    { label: 'Managed heap', value: byteSpan(runtime.startedManagedBytes, runtime.managedBytes, runtime.managedDeltaBytes) },
    { label: 'GC heap', value: byteSpan(runtime.startedGcHeapBytes, runtime.gcHeapBytes, runtime.gcHeapDeltaBytes) },
    { label: 'Committed memory', value: byteSpan(runtime.startedCommittedBytes, runtime.committedBytes, runtime.committedDeltaBytes) },
    { label: 'Fragmented memory', value: byteSpan(runtime.startedFragmentedBytes, runtime.fragmentedBytes, runtime.fragmentedDeltaBytes) },
    { label: 'Private bytes', value: byteSpan(runtime.startedPrivateBytes, runtime.privateBytes, runtime.privateDeltaBytes) },
  ];
  if (!pending) {
    rows.push(
      { label: 'Allocated during the attempt', value: formatByteSize(runtime.allocatedBytes ?? 0) },
      {
        label: 'GC collections',
        value: `${runtime.gen0Collections ?? 0} / ${runtime.gen1Collections ?? 0} / ${runtime.gen2Collections ?? 0}`,
      },
      { label: 'CPU time', value: formatCpu(runtime.cpuMilliseconds ?? 0) },
    );
  }
  return {
    pending,
    rows,
    rssDeltaLabel: runtime.workingSetDeltaBytes == null ? null : `RSS ${formatSignedBytes(runtime.workingSetDeltaBytes)}`,
  };
}

export function assignmentRuntimeView(commands: AssignmentRuntimeCommand[]): AssignmentRuntimeView | null {
  let highest: { bytes: number; step: string; attempt: string } | null = null;
  let change: { bytes: number; step: string; attempt: string } | null = null;
  for (const command of commands) {
    const runtime = command.runtime;
    if (!runtime) {
      continue;
    }
    const readings = [runtime.startedWorkingSetBytes, runtime.workingSetBytes]
      .filter((value): value is number => value != null);
    const rss = readings.length ? Math.max(...readings) : null;
    if (rss != null && (highest == null || rss > highest.bytes)) {
      highest = { bytes: rss, step: command.stepKey, attempt: String(command.attempt) };
    }
    if (runtime.workingSetDeltaBytes != null && (change == null || runtime.workingSetDeltaBytes > change.bytes)) {
      change = {
        bytes: runtime.workingSetDeltaBytes,
        step: command.stepKey,
        attempt: String(command.attempt),
      };
    }
  }
  if (!highest) {
    return null;
  }
  return {
    highestRss: formatByteSize(highest.bytes),
    highestStep: highest.step,
    highestAttempt: highest.attempt,
    change: change ? formatSignedBytes(change.bytes) : null,
    changeStep: change?.step ?? null,
    changeAttempt: change?.attempt ?? null,
  };
}

function byteSpan(
  start: number | null | undefined,
  end: number | null | undefined,
  delta: number | null | undefined,
): string {
  if (start == null && end == null) {
    return '—';
  }
  if (end == null) {
    return formatByteSize(start ?? 0);
  }
  if (start == null) {
    return formatByteSize(end);
  }
  const span = `${formatByteSize(start)} → ${formatByteSize(end)}`;
  return delta == null ? span : `${span} (${formatSignedBytes(delta)})`;
}

function formatSignedBytes(bytes: number): string {
  if (bytes > 0) {
    return `+${formatByteSize(bytes)}`;
  }
  if (bytes < 0) {
    return `-${formatByteSize(Math.abs(bytes))}`;
  }
  return formatByteSize(0);
}

function formatByteSize(bytes: number): string {
  if (bytes >= GIB) {
    return `${(bytes / GIB).toFixed(2)} GiB`;
  }
  if (bytes >= MIB) {
    return `${(bytes / MIB).toFixed(2)} MiB`;
  }
  if (bytes >= KIB) {
    return `${(bytes / KIB).toFixed(2)} KiB`;
  }
  return `${bytes} B`;
}

function formatCpu(milliseconds: number): string {
  if (milliseconds < 1000) {
    return `${milliseconds} ms`;
  }
  return `${(milliseconds / 1000).toFixed(2)} s`;
}
